/**
 * Workflows list view — /workflows
 *
 * Lists the workspace's routines with search, a card/list view toggle, sort, and a status filter, built
 * on the shared list-page pattern (`useListControls` + `ListToolbar` + `VirtualizedCards` / `Table`).
 * The card grid and the table both virtualize, so a large workspace stays smooth. Delete is admin-only,
 * confirmed via a dialog.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Zap } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { ConfirmDialog } from '@/_shared/components/ConfirmDialog';
import { ListEmpty, ListError, ListNoMatches, ListSkeleton } from '@/_shared/components/list/ListStates';
import { ListToolbar } from '@/_shared/components/list/ListToolbar';
import { VirtualizedCards } from '@/_shared/components/list/VirtualizedCards';
import { Table } from '@/_shared/components/Table';
import { Button } from '@/_shared/components/ui/button';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useListControls } from '@/_shared/hooks/useListControls';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';
import { filterSortItems, type SortState } from '@/_shared/lib/list-controls';

import { WorkflowCard } from '../components/WorkflowCard';
import { buildWorkflowColumns } from '../components/workflowColumns';
import { useChannelOptions } from '../hooks/useChannelOptions';
import {
  makeWorkflowDeriveConfig,
  WORKFLOW_DEFAULT_SORT,
  WORKFLOW_FILTER_GROUPS,
  WORKFLOW_FILTER_KEYS,
  WORKFLOW_FILTER_VALUES,
  WORKFLOW_SORT_KEYS,
  WORKFLOW_SORT_OPTIONS,
  type WorkflowFilterKey,
  type WorkflowListItem,
  type WorkflowSortKey,
} from '../lib/workflowList';

export function Workflows(): React.ReactElement {
  usePageTitle('Workflows');
  const { wp, isAdmin } = useWorkspace();
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery(orpc.workflow.list.queryOptions({ input: {} }));
  const workflows = useMemo<WorkflowListItem[]>(() => data?.workflows ?? [], [data]);

  // Slack channel id → "#name" for the destination label + search. Degrades gracefully for non-admins
  // (listChannelBindings is admin-only) — falls back to the id, then a plain "Slack" label. Prebuilt as a
  // Map so per-row resolution (once per routine in the search pass) stays O(1), not a linear scan.
  const { options: channelOptions } = useChannelOptions();
  const channelLabels = useMemo(() => new Map(channelOptions.map((c) => [c.value, c.label])), [channelOptions]);
  const resolveChannel = useCallback(
    (channelId: string | null): string | null => (channelId ? (channelLabels.get(channelId) ?? channelId) : null),
    [channelLabels],
  );

  // ── Controls (search + view + sort + status filter) ──
  const controls = useListControls<WorkflowSortKey, WorkflowFilterKey>({
    sortKeys: WORKFLOW_SORT_KEYS,
    defaultSort: WORKFLOW_DEFAULT_SORT,
    filterKeys: WORKFLOW_FILTER_KEYS,
    filterOptions: WORKFLOW_FILTER_VALUES,
    persistKey: 'workflows',
  });
  const deriveConfig = useMemo(() => makeWorkflowDeriveConfig(resolveChannel), [resolveChannel]);
  // `controls.query` is already debounced by the toolbar's search box, so this recomputes on the debounce,
  // not per keystroke. `view` is intentionally not part of this derivation — `filterSortItems` ignores it,
  // so toggling the card/list view must not re-run the filter + sort over the whole list.
  const visible = useMemo(
    () =>
      filterSortItems(
        workflows,
        { query: controls.query, sort: controls.sort, filters: controls.filters },
        deriveConfig,
      ),
    [workflows, controls.query, controls.sort, controls.filters, deriveConfig],
  );

  // ── Delete (admin-only, confirmed) ──
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Only resolved while the dialog is open — otherwise this scans the whole list on every keystroke.
  const deletingWorkflow = deletingId ? (workflows.find((w) => w.id === deletingId) ?? null) : null;
  const onDelete = useCallback((id: string) => setDeletingId(id), []);
  const deleteMutation = useMutation(
    orpc.workflow.delete.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: orpc.workflow.list.queryOptions({ input: {} }).queryKey });
        toast.success('Routine deleted');
        setDeletingId(null);
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : 'Failed to delete routine');
      },
    }),
  );

  const columns = useMemo(
    () => buildWorkflowColumns({ isAdmin, onDelete, resolveChannel }),
    [isAdmin, onDelete, resolveChannel],
  );

  // Stable identities for the row/card callbacks — a fresh lambda each render would make the virtualized
  // Table / card grid reconcile every mounted row on unrelated re-renders (e.g. each search keystroke).
  const getRowId = useCallback((wf: WorkflowListItem) => wf.id, []);
  const openWorkflow = useCallback((wf: WorkflowListItem) => navigate(wp(`/workflows/${wf.id}`)), [navigate, wp]);
  const renderCard = useCallback(
    (wf: WorkflowListItem) => (
      <WorkflowCard
        workflow={wf}
        to={wp(`/workflows/${wf.id}`)}
        isAdmin={isAdmin}
        onDelete={onDelete}
        resolveChannel={resolveChannel}
      />
    ),
    [wp, isAdmin, onDelete, resolveChannel],
  );

  const hasWorkflows = workflows.length > 0;

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <header className="flex items-center gap-3 border-b px-6 py-4">
        <h1 className="text-lg font-semibold tracking-tight text-foreground">Workflows</h1>
        <div className="ml-auto">
          <Button variant="primary" asChild>
            <Link to={wp('/workflows/new')}>
              <Plus className="size-4" />
              New workflow
            </Link>
          </Button>
        </div>
      </header>

      {error && !hasWorkflows ? (
        // Only take over the whole page when there's nothing to show. A background refetch that fails
        // (window refocus past staleTime, or a post-delete invalidation) keeps `data` cached but sets
        // `error` — don't blank the still-usable list/toolbar behind a full-screen error in that case.
        <ListError
          title="Couldn't load workflows"
          message={error instanceof Error ? error.message : 'Something went wrong.'}
        />
      ) : isLoading ? (
        <ListSkeleton view={controls.view} />
      ) : !hasWorkflows ? (
        <ListEmpty
          icon={Zap}
          title="No workflows yet"
          description="Create your first workflow to automate Slack notifications from your warehouse."
        >
          <Button variant="primary" asChild>
            <Link to={wp('/workflows/new')}>
              <Plus className="size-4" />
              New workflow
            </Link>
          </Button>
        </ListEmpty>
      ) : (
        <>
          <div className="pt-4 pb-1">
            <div className="mx-auto max-w-5xl px-6">
              <ListToolbar
                query={controls.query}
                onQueryChange={controls.setQuery}
                searchPlaceholder="Search routines…"
                view={controls.view}
                onViewChange={controls.setView}
                sortOptions={WORKFLOW_SORT_OPTIONS}
                sort={controls.sort}
                onSortChange={(next) => controls.setSort(next as SortState<WorkflowSortKey>)}
                defaultSort={WORKFLOW_DEFAULT_SORT}
                filterGroups={WORKFLOW_FILTER_GROUPS}
                filters={controls.filters}
                onFilterChange={(key, value) => controls.setFilter(key as WorkflowFilterKey, value)}
                onClearFilters={controls.clearFilters}
                onClearAll={controls.clearAll}
                totalCount={workflows.length}
                resultCount={visible.length}
                itemNoun={{ singular: 'routine', plural: 'routines' }}
              />
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-hidden">
            {visible.length === 0 ? (
              <ListNoMatches
                noun="routines"
                onClear={() => {
                  controls.setQuery('');
                  controls.clearFilters();
                }}
              />
            ) : controls.view === 'list' ? (
              <div className="mx-auto h-full max-w-5xl px-6 py-4">
                <Table
                  data={visible}
                  columns={columns}
                  getRowId={getRowId}
                  onRowClick={openWorkflow}
                  hideHeader
                  fixedLayout
                  fillHeight
                  emptyState="No routines"
                />
              </div>
            ) : (
              <VirtualizedCards
                items={visible}
                getItemKey={getRowId}
                minColumnRem={20}
                maxColumns={3}
                maxContentRem={64}
                estimateRowHeight={168}
                className="h-full"
                renderItem={renderCard}
              />
            )}
          </div>
        </>
      )}

      <ConfirmDialog
        open={deletingId !== null}
        onOpenChange={(next) => {
          if (!next) setDeletingId(null);
        }}
        title="Delete this routine?"
        description={
          <>
            This permanently deletes {deletingWorkflow ? `"${deletingWorkflow.name}"` : 'the routine'}, its run history,
            and its RoutineBuilder conversation. This can&apos;t be undone.
          </>
        }
        loading={deleteMutation.isPending}
        onConfirm={() => deletingId && deleteMutation.mutate({ id: deletingId })}
      />
    </div>
  );
}
